import { MessageDescriptor } from 'react-intl';

import getUrl from '@/utils/getUrl';

import b2bMessages from './messages';

export interface Message {
	id: string;
	defaultMessage: string;
	description: string;
}

interface DtcSubscribeProps {
	faqLink: string;
	errorLink: string;
	logo: string | null;
	disableGradient: boolean;
	disableSocialSignup: boolean;
	successRedirectUrl?: string | null;
	messages: {
		headline?: MessageDescriptor | null;
		subheadline?: MessageDescriptor | null;
		activate: MessageDescriptor | string | null;
		instructions?: MessageDescriptor | null;
		help?: MessageDescriptor | null;
		legal?: MessageDescriptor | null;
		error?: MessageDescriptor | null;
		successText?: MessageDescriptor | null;
		successCTA?: MessageDescriptor | null;
		requestAccess?: MessageDescriptor | null;
		errorRequestAccess?: MessageDescriptor | null;
		errorInvalidId?: MessageDescriptor | null;
	};
	successCTAUrl?: string;
	partnerTerms?: string;
	endDate?: string;
}

const CALM_HELP_CENTER = 'https://calm.com/help';
export const CONTACT_SUPPORT = 'https://support.calm.com/hc/en-us';

const singtelSubscribeBase: Omit<DtcSubscribeProps, 'messages'> = {
	faqLink: CONTACT_SUPPORT,
	errorLink: CALM_HELP_CENTER,
	logo: '/_n/images/b2b/logos/singtel-logo.webp',
	disableGradient: false,
	disableSocialSignup: false,
};

const verizonSubscribe: DtcSubscribeProps = {
	faqLink: CONTACT_SUPPORT,
	errorLink: CALM_HELP_CENTER,
	messages: {
		headline: b2bMessages.defaultTitle,
		subheadline: null,
		activate: b2bMessages.offerMessage,
	},
	logo: '/_n/images/b2b/logos/verizon-logo.webp',
	disableGradient: false,
	disableSocialSignup: false,
	successRedirectUrl: 'https://www.verizon.com/digital/nsa/secure/ui/products/ehub/manage-dashboard',
};

export const partners: Record<string, DtcSubscribeProps> = {
	lincoln: {
		faqLink: CONTACT_SUPPORT,
		errorLink: 'https://lincoln.com',
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.activateLincolnMessage,
		},
		logo: '/_n/images/b2b/logos/lincoln.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	telus: {
		faqLink: CONTACT_SUPPORT,
		errorLink: 'https://www.telus.com/my-account/tv/overview/optik',
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: null,
		disableGradient: false,
		disableSocialSignup: false,
	},
	default: {
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: null,
		disableGradient: false,
		disableSocialSignup: false,
	},
	'blue-apron': {
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.blueApronOfferMessage,
		},
		logo: '/_n/images/b2b/logos/blueapron.webp',
		disableGradient: true,
		disableSocialSignup: false,
	},
	optus: {
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.optusOfferMessage,
		},
		logo: '/_n/images/b2b/logos/optus.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	'verizon-1y': verizonSubscribe,
	'verizon-1m': verizonSubscribe,
	gerber: {
		faqLink: 'https://support.calm.com/hc/en-us',
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
			successText: b2bMessages.gerberSuccessCopy,
			successCTA: b2bMessages.gerberSuccessCTA,
		},
		logo: '/_n/images/Gerber_BabyHead.png',
		disableGradient: false,
		disableSocialSignup: false,
		successCTAUrl: `${getUrl('www')}/packs/feeds/collection-mindful-parenting`,
	},
	'singtel-1m': {
		...singtelSubscribeBase,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.singtel1mActivate,
		},
	},
	'singtel-3m': {
		...singtelSubscribeBase,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.singtel3mActivate,
		},
	},
	'singtel-1y': {
		...singtelSubscribeBase,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.singtel1yActivate,
		},
	},
	chegg: {
		faqLink: 'https://support.calm.com/hc/en-us/articles/9040502047515',
		errorLink: CALM_HELP_CENTER,
		logo: '/_n/images/dtc-partners/chegg.png',
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		disableGradient: false,
		disableSocialSignup: false,
	},
	fifa: {
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: '/_n/images/dtc-partners/fifa-footer-logo-trimmed.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
	'fifa-teams': {
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: '/_n/images/dtc-partners/fifa-footer-logo-trimmed.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
	'fifa-volunteers': {
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: '/_n/images/dtc-partners/fifa-footer-logo-trimmed.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
	ee_1y: {
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.eeActivate,
		},
		logo: '/_n/images/dtc-partners/EE-logo.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
	ee_1m: {
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.eeActivate,
		},
		logo: '/_n/images/dtc-partners/EE-logo.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
} as const;
type Partner = keyof typeof partners;
export const isPartner = (value: string): value is Partner => value in partners;
